import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Divider,
  Heading,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip,
  useToast,
  VStack,
} from "@chakra-ui/react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  FaChevronDown,
  FaChevronRight,
  FaCopy,
  FaDownload,
} from "react-icons/fa";
import { apiUrl } from "../config";
import { resolveTemplateType } from "../constants/templateTypes";

const authHeader = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("idToken")}`,
});

// Read-only list of templates curated by platform admins, grouped by
// category and sorted alphabetically within each group. Each template
// exposes two copy buttons: raw markdown and rich (HTML) for pasting into
// markdown-aware tools vs. rich-text editors like Gmail.
const TemplatesLibrary = () => {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${apiUrl}/templates`, {
          headers: authHeader(),
        });
        if (!cancelled) setTemplates(res.data.templates || []);
      } catch (err) {
        console.error("Load templates failed", err);
        if (!cancelled) {
          toast({
            title: "Failed to load templates",
            description: err?.response?.data?.error || err.message,
            status: "error",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const grouped = useMemo(() => {
    const byCategory = new Map();
    for (const t of templates) {
      const cat = t.category || "Uncategorized";
      if (!byCategory.has(cat)) byCategory.set(cat, []);
      byCategory.get(cat).push(t);
    }
    const categories = [...byCategory.keys()].sort((a, b) =>
      a.localeCompare(b),
    );
    return categories.map((cat) => ({
      category: cat,
      items: byCategory
        .get(cat)
        .slice()
        .sort((a, b) => (a.title || "").localeCompare(b.title || "")),
    }));
  }, [templates]);

  if (loading) {
    return (
      <HStack justify="center" py={10}>
        <Spinner />
        <Text>Loading templates…</Text>
      </HStack>
    );
  }

  return (
    <Box>
      <Box mb={6}>
        <Text color="gray.700">
          Ready-to-use copy for your campaign — emails, social media posts,
          business outreach and more. Grab the markdown for markdown-aware
          tools, or the rich version for Gmail, docs or anywhere else you
          paste styled text. Tweak the wording to fit your voice before you
          send.
        </Text>
      </Box>

      {grouped.length === 0 ? (
        <Text color="gray.500" py={4}>
          No templates yet. Ask a platform admin to add some.
        </Text>
      ) : (
        <VStack align="stretch" spacing={8}>
          {grouped.map(({ category, items }) => (
            <Box key={category}>
              <Heading size="md" mb={3} color="heading">
                {category}
              </Heading>
              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                spacing={4}
                alignItems="flex-start"
              >
                {items.map((tpl) => (
                  <TemplateCard key={tpl.id} template={tpl} />
                ))}
              </SimpleGrid>
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );
};

const TemplateCard = ({ template }) => {
  const toast = useToast();
  const renderedRef = useRef(null);
  const [expanded, setExpanded] = useState(false);

  const copyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(template.contents || "");
      toast({ title: "Markdown copied", status: "success", duration: 2000 });
    } catch (err) {
      toast({
        title: "Copy failed",
        description: err.message,
        status: "error",
      });
    }
  };

  const copyRich = async () => {
    const el = renderedRef.current;
    if (!el) return;
    const html = el.innerHTML;
    // textContent (not innerText) so collapsed cards still yield a plain
    // fallback — Chakra Collapse hides the box via max-height:0 which zeroes
    // out innerText.
    const text = el.textContent || "";
    try {
      if (
        typeof ClipboardItem !== "undefined" &&
        navigator.clipboard &&
        navigator.clipboard.write
      ) {
        const item = new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        });
        await navigator.clipboard.write([item]);
      } else {
        // Fallback: text-only clipboard
        await navigator.clipboard.writeText(text);
      }
      toast({ title: "Rich text copied", status: "success", duration: 2000 });
    } catch (err) {
      toast({
        title: "Copy failed",
        description: err.message,
        status: "error",
      });
    }
  };

  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderRadius="lg"
      p={{ base: 3, md: 4 }}
      boxShadow="sm"
    >
      <HStack
        spacing={2}
        mb={expanded ? 3 : 0}
        cursor="pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <IconButton
          size="xs"
          variant="ghost"
          aria-label={expanded ? "Collapse" : "Expand"}
          icon={expanded ? <FaChevronDown /> : <FaChevronRight />}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
        />
        <Tooltip
          label={resolveTemplateType(template.type).label}
          openDelay={200}
          hasArrow
        >
          <Box
            color="gray.700"
            display="inline-flex"
            alignItems="center"
            fontSize="lg"
            onClick={(e) => e.stopPropagation()}
            cursor="help"
          >
            <Icon as={resolveTemplateType(template.type).icon} />
          </Box>
        </Tooltip>
        <Heading size="sm" isTruncated>
          {template.title}
        </Heading>
      </HStack>
      <Collapse in={expanded} animateOpacity>
        <Divider mb={3} />
        <Box
          ref={renderedRef}
          className="template-markdown"
          sx={{
          "h1, h2, h3, h4": {
            fontWeight: "semibold",
            mt: 3,
            mb: 2,
          },
          h1: { fontSize: "xl" },
          h2: { fontSize: "lg" },
          h3: { fontSize: "md" },
          p: { my: 2 },
          "ul, ol": { pl: 6, my: 2 },
          li: { my: 1 },
          a: { color: "green.600", textDecoration: "underline" },
          blockquote: {
            borderLeft: "4px solid",
            borderColor: "gray.200",
            pl: 3,
            color: "gray.600",
            my: 2,
          },
          code: {
            bg: "gray.100",
            px: 1,
            borderRadius: "sm",
            fontFamily: "mono",
            fontSize: "sm",
          },
          "pre code": {
            display: "block",
            p: 3,
            overflowX: "auto",
          },
          hr: { my: 4, borderColor: "gray.200" },
          img: {
            maxWidth: "100%",
            height: "auto",
            my: 2,
            borderRadius: "sm",
          },
          table: {
            width: "100%",
            my: 3,
            borderCollapse: "collapse",
          },
          "th, td": {
            border: "1px solid",
            borderColor: "gray.200",
            px: 2,
            py: 1,
            textAlign: "left",
          },
        }}
      >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {template.contents || ""}
          </ReactMarkdown>
        </Box>
        {(template.images || []).length > 0 && (
          <Attachments images={template.images} />
        )}
        <HStack
          mt={4}
          pt={3}
          borderTopWidth="1px"
          borderColor="gray.100"
          justify="flex-end"
          spacing={2}
          flexWrap="wrap"
        >
          <Button
            size="xs"
            variant="outline"
            leftIcon={<FaCopy />}
            onClick={copyMarkdown}
          >
            Copy Markdown
          </Button>
          <Button
            size="xs"
            variant="primary"
            leftIcon={<FaCopy />}
            onClick={copyRich}
          >
            Copy Rich
          </Button>
        </HStack>
      </Collapse>
    </Box>
  );
};

const Attachments = ({ images }) => {
  const toast = useToast();

  const download = async (img) => {
    try {
      const res = await fetch(img.url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = img.filename || "image";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      toast({
        title: "Download failed",
        description: err.message,
        status: "error",
      });
    }
  };

  return (
    <Box mt={4} pt={4} borderTopWidth="1px" borderColor="gray.100">
      <Heading size="xs" mb={2} color="gray.600" textTransform="uppercase">
        Attachments
      </Heading>
      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
        {images.map((img, idx) => (
          <Box
            key={`${img.url}-${idx}`}
            borderWidth="1px"
            borderRadius="md"
            p={2}
            bg="gray.50"
          >
            <Box
              as="a"
              href={img.url}
              target="_blank"
              rel="noopener noreferrer"
              display="flex"
              alignItems="center"
              justifyContent="center"
              mb={2}
              minH="140px"
              bg="white"
              borderRadius="sm"
              overflow="hidden"
            >
              <Box
                as="img"
                src={img.url}
                alt={img.filename}
                maxW="100%"
                maxH="260px"
                w="auto"
                h="auto"
                objectFit="contain"
              />
            </Box>
            <Text
              fontSize="xs"
              noOfLines={1}
              title={img.filename}
              mb={1}
              color="gray.700"
            >
              {img.filename}
            </Text>
            <Button
              size="xs"
              variant="outline"
              leftIcon={<FaDownload />}
              width="100%"
              onClick={() => download(img)}
            >
              Download
            </Button>
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
};

export default TemplatesLibrary;
